import * as XLSX from 'xlsx';
import { supabase } from '../lib/supabase';
import type { Registry, RegistryItem } from '../types/registry';

interface ClaimInfo {
  name: string;
  email: string;
  type: 'user' | 'guest' | 'payhere';
}

interface Contribution {
  contributor_name: string;
  amount: number;
  message?: string;
}

export async function exportRegistryToExcel(registry: Registry & { items: RegistryItem[] }) {
  // Get claim information and contributions for all items
  const itemsWithDetails = await Promise.all(registry.items.map(async item => {
    let claimInfo: ClaimInfo | null = null;
    let contributions: Contribution[] = [];

    if (item.is_claimed) {
      if (item.claimed_by) {
        // Get user info
        const { data: userData } = await supabase
          .from('profiles')
          .select('username, email')
          .eq('id', item.claimed_by)
          .single();

        if (userData) {
          claimInfo = {
            name: userData.username,
            email: userData.email || '',
            type: 'user'
          };
        }
      } else if (item.guest_claimed_by) {
        // Get guest info
        const { data: guestData } = await supabase
          .from('guest_users')
          .select('name, email')
          .eq('id', item.guest_claimed_by)
          .single();

        if (guestData) {
          claimInfo = {
            name: guestData.name,
            email: guestData.email,
            type: 'guest'
          };
        }
      } else {
        // PayHere payment — look up registry_claims
        const { data: claimData } = await supabase
          .from('registry_claims')
          .select('claimer_name')
          .eq('item_id', item.id)
          .order('claimed_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (claimData) {
          claimInfo = {
            name: claimData.claimer_name,
            email: '',
            type: 'payhere'
          };
        }
      }
    }

    // Get group gift contributions if applicable
    if (item.allows_group_gifting) {
      const { data: contributionsData } = await supabase
        .from('group_gift_contributions')
        .select('contributor_name, amount, message')
        .eq('item_id', item.id)
        .order('created_at', { ascending: true });

      if (contributionsData) {
        contributions = contributionsData;
      }
    }

    return {
      ...item,
      claimInfo,
      contributions
    };
  }));

  // Prepare data for Excel
  const excelData = itemsWithDetails.map(item => {
    const baseData = {
      'Item Name': item.name,
      'Price': `${registry.currency} ${item.price}`,
      'Priority': item.priority,
      'Status': item.is_claimed ? 'Claimed' : 'Available',
      'Product URL': item.url || '',
      'Group Gift': item.allows_group_gifting ? 'Yes' : 'No',
    };

    if (item.allows_group_gifting) {
      baseData['Target Amount'] = `${registry.currency} ${item.target_amount}`;
      baseData['Collected Amount'] = `${registry.currency} ${item.collected_amount}`;
      
      // Add contributors information
      if (item.contributions.length > 0) {
        baseData['Contributors'] = item.contributions
          .map(c => `${c.contributor_name} (${registry.currency} ${c.amount})`)
          .join('\n');
        baseData['Contribution Messages'] = item.contributions
          .filter(c => c.message)
          .map(c => `${c.contributor_name}: ${c.message}`)
          .join('\n');
      }
    } else {
      baseData['Claimed By'] = item.claimInfo?.name || '';
      baseData['Claimer Email'] = item.claimInfo?.email || '';
      baseData['Claimer Type'] = item.claimInfo?.type === 'guest' ? 'Guest' :
                                item.claimInfo?.type === 'user' ? 'Registered User' :
                                item.claimInfo?.type === 'payhere' ? 'PayHere Payment' : '';
    }

    return baseData;
  });

  // Create workbook
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(excelData);

  // Adjust column widths
  const colWidths = [
    { wch: 30 }, // Item Name
    { wch: 15 }, // Price
    { wch: 10 }, // Priority
    { wch: 10 }, // Status
    { wch: 40 }, // Product URL
    { wch: 10 }, // Group Gift
    { wch: 15 }, // Target Amount
    { wch: 15 }, // Collected Amount
    { wch: 40 }, // Contributors
    { wch: 50 }, // Contribution Messages
    { wch: 25 }, // Claimed By
    { wch: 30 }, // Claimer Email
    { wch: 15 }  // Claimer Type
  ];
  ws['!cols'] = colWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Registry Items');

  // Generate Excel file
  XLSX.writeFile(wb, `${registry.title} - Gift Registry.xlsx`);
}